import { useEffect, useRef, useState } from "react";

/* ------------------------------------------------------------------ *
 * MountainPeak Ventures
 *
 * The interesting fact about this company is structural: it is one LLC
 * that holds several independently-named ventures, each solving a
 * different, specific problem. So the ventures index IS the page, and
 * the mark is several peak outlines converging into one.
 *
 * Everything below is driven by these two arrays - edit them, not the
 * markup, to change what the site says.
 * ------------------------------------------------------------------ */

// Nothing here names a venture. The companies under MountainPeak are still in
// development, and naming a business before its product exists announces it
// prematurely. These describe the SHAPE of work taken on, which is honest,
// useful to a reader, and gives nothing away.
const WORK = [
  {
    title: "The tool that should exist but doesn't",
    body:
      "Somebody is doing a job with a spreadsheet, a group chat and a lot of copying and pasting. The software for it either doesn't exist or is built for companies a hundred times their size.",
  },
  {
    title: "The thing one family actually needs",
    body:
      "Most of what gets built here starts with a single household or a single small organisation with a specific, slightly awkward problem. If it helps them, it usually helps others with the same problem.",
  },
  {
    title: "Small enough to finish",
    body:
      "Work that can reach a real, usable version quickly, rather than a platform that needs a team and a year before anybody can try it.",
  },
  {
    title: "Something worth running for years",
    body:
      "Built to be hosted and maintained here for the long haul, not handed over half-finished. If it can't be kept running, it doesn't get started.",
  },
];

const PRINCIPLES = [
  {
    k: "Start with one person",
    v: "Every venture here began as a specific problem for a specific person. Build the thing that actually helps them, then find out who else has the same problem.",
  },
  {
    k: "Ship small, ship real",
    v: "A working version in front of a real user beats a roadmap. Most of these started as something used by one family before it was a product.",
  },
  {
    k: "Own the whole stack",
    v: "Design, build, host and run it. No handoffs, no waiting on a vendor to fix something that matters today.",
  },
  {
    k: "Keep it maintainable",
    v: "Small, boring, well-understood systems outlast clever ones. Things here are expected to still work in five years without attention.",
  },
];

/* ---------- the mark: N outlines converging into one peak ---------- */

function PeakMark({ size = 96, layers = 3, animate = true }) {
  return (
    <svg
      className={"mark" + (animate ? " mark--in" : "")}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label="MountainPeak Ventures"
    >
      {/* the offset outlines are the other ventures, resolving into the front peak */}
      {Array.from({ length: layers - 1 }, (_, i) => (
        <polygon
          key={i}
          className="mark__ghost"
          style={{ "--i": i + 1 }}
          points="32,8 58,54 6,54"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      ))}
      <polygon
        className="mark__front"
        points="32,8 58,54 6,54"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <polygon className="mark__fill" points="32,24 47,54 17,54" fill="currentColor" opacity="0.16" />
    </svg>
  );
}

/* ---------- reveal-on-scroll, reduced-motion aware ---------- */

function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setSeen(true);
      return;
    }

    // Reveal anything already at or above the fold straight away.
    if (el.getBoundingClientRect().top < window.innerHeight) {
      setSeen(true);
      return;
    }

    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setSeen(true), obs.disconnect()),
      { rootMargin: "-60px" }
    );
    obs.observe(el);

    // Hard safety net. IntersectionObserver only fires while an element is
    // intersecting, so anything the viewport skips over - an in-page anchor, a
    // restored scroll position, a fast flick - can end up invisible forever.
    // A decorative animation must never be able to hide content, so reveal
    // unconditionally shortly after mount regardless of what the observer saw.
    const failsafe = setTimeout(() => setSeen(true), 1400);

    return () => {
      obs.disconnect();
      clearTimeout(failsafe);
    };
  }, []);
  return [ref, seen];
}

function Reveal({ children, delay = 0, as: Tag = "div", className = "" }) {
  const [ref, seen] = useReveal();
  return (
    <Tag
      ref={ref}
      className={`reveal ${seen ? "reveal--in" : ""} ${className}`}
      style={{ "--d": `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

/* ---------- sections ---------- */

function Nav() {
  return (
    <header className="nav">
      <a className="nav__brand" href="#top">
        <PeakMark size={26} layers={3} animate={false} />
        <span>
          MountainPeak <em>Ventures</em>
        </span>
      </a>
      <nav className="nav__links">
        <a href="#work">What we do</a>
        <a href="#approach">Approach</a>
        <a href="#contact">Contact</a>
      </nav>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" id="top">
      <div className="hero__mark">
        <PeakMark size={150} layers={4} />
      </div>
      <p className="eyebrow">MountainPeak Ventures LLC</p>
      <h1>
        A few small companies,
        <br />
        <em>one workshop.</em>
      </h1>
      <p className="hero__lead">
        MountainPeak is the parent company for a handful of independently-named
        ventures. Each one exists because somebody had a real problem and there
        wasn&rsquo;t a good tool for it. We design, build, host and run them
        ourselves.
      </p>
      <div className="hero__cta">
        <a className="btn" href="#work">
          What we take on
        </a>
      </div>
    </section>
  );
}

function Work() {
  return (
    <section className="section" id="work">
      <Reveal as="div" className="section__head">
        <p className="eyebrow">What we take on</p>
        <h2>Specific problems, not markets.</h2>
        <p className="section__lead">
          MountainPeak doesn&rsquo;t chase categories. Every project starts with
          a real person describing something that is genuinely annoying to do,
          and ends with software that makes it not annoying.
        </p>
      </Reveal>

      <ol className="worklist">
        {WORK.map((w, i) => (
          <Reveal as="li" key={w.title} delay={i * 80} className="work">
            <div className="work__bar" aria-hidden="true" />
            <span className="work__num">{String(i + 1).padStart(2, "0")}</span>
            <div className="work__body">
              <h3>{w.title}</h3>
              <p>{w.body}</p>
            </div>
          </Reveal>
        ))}
      </ol>

      <Reveal as="p" className="note" delay={120}>
        <span className="note__dot" aria-hidden="true" />
        Several ventures are in development under MountainPeak. They&rsquo;ll be
        named here when there&rsquo;s something real to try.
      </Reveal>
    </section>
  );
}

function Approach() {
  return (
    <section className="section--dark" id="approach">
      <div className="wrap">
        <Reveal as="div" className="section__head">
          <p className="eyebrow">How we work</p>
          <h2>Build the useful thing first.</h2>
        </Reveal>
        <dl className="principles">
        {PRINCIPLES.map((p, i) => (
          <Reveal as="div" key={p.k} delay={i * 80} className="principle">
            <dt>{p.k}</dt>
            <dd>{p.v}</dd>
          </Reveal>
          ))}
        </dl>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section className="section section--contact" id="contact">
      <Reveal>
        <p className="eyebrow">Contact</p>
        <h2>
          Got a problem that
          <br />
          needs building?
        </h2>
        <p className="section__lead">
          If you have something specific and slightly awkward that off-the-shelf
          software doesn&rsquo;t handle, that&rsquo;s usually the interesting
          kind.
        </p>
        {/* No address published while this is a holding page - it would only be
            harvested. Deliberately not a form either: one that silently discards
            messages is worse than none. */}
        <p className="note note--contact">
          <span className="note__dot" aria-hidden="true" />
          Contact details will be published here once the first venture launches.
        </p>
      </Reveal>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <PeakMark size={20} layers={3} animate={false} />
      <span>MountainPeak Ventures LLC</span>
      <span className="footer__sp" />
      <span className="footer__muted">
        &copy; {new Date().getFullYear()}
      </span>
    </footer>
  );
}

export default function App() {
  return (
    <>
      <style>{CSS}</style>
      <div className="page">
        <Nav />
        <main>
          <Hero />
          <Work />
          <Approach />
          <Contact />
        </main>
        <Footer />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

const CSS = `
@import url('/assets/fonts/fonts.css');

:root{
  --paper:#eef0ee;
  --paper-2:#f7f8f7;
  --ink:#14181b;
  --ink-2:#4c5559;
  --line:#d3d8d4;
  --blue:#2233e6;
  --blue-ink:#1421a8;
  --signal:#e8552f;
  --dark:#11161a;

  --display:'Bricolage Grotesque',system-ui,sans-serif;
  --body:'IBM Plex Sans',system-ui,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,monospace;

  --pad: clamp(20px, 5vw, 64px);
  --maxw: 1180px;
}

*{box-sizing:border-box}
html{scroll-behavior:smooth}
body{margin:0;background:var(--paper);color:var(--ink);font-family:var(--body);
  -webkit-font-smoothing:antialiased;font-size:16px;line-height:1.6}
h1,h2,h3{font-family:var(--display);font-weight:800;letter-spacing:-.03em;line-height:.98;margin:0}
em{font-style:normal}
a{color:inherit}
::selection{background:var(--blue);color:#fff}
:focus-visible{outline:2.5px solid var(--blue);outline-offset:3px}

.page{max-width:100%;overflow-x:hidden}

/* ---------- mark ---------- */
.mark{display:block;color:var(--blue);overflow:visible}
/* the ghosts keep a permanent offset - the point of the mark is that several
   outlines sit behind one peak, and that has to read at rest, not only while
   the entrance animation is playing (or at all, under reduced motion). */
.mark__ghost{opacity:.3;transform-origin:32px 32px;
  transform:translate(calc(var(--i) * 4px), calc(var(--i) * -2.5px))}
.mark--in .mark__ghost{
  animation:converge 1100ms cubic-bezier(.2,.7,.2,1) both;
  animation-delay:calc(var(--i) * 90ms);
}
@keyframes converge{
  from{transform:translate(calc(var(--i) * 13px), calc(var(--i) * -8px)) scale(1.05);opacity:0}
  to{transform:translate(calc(var(--i) * 4px), calc(var(--i) * -2.5px));opacity:.3}
}
.mark--in .mark__front{stroke-dasharray:170;stroke-dashoffset:170;
  animation:draw 1200ms cubic-bezier(.4,0,.2,1) 200ms both}
@keyframes draw{to{stroke-dashoffset:0}}
.mark--in .mark__fill{animation:fadein 700ms ease 900ms both}
@keyframes fadein{from{opacity:0}to{opacity:.16}}

/* ---------- nav ---------- */
.nav{position:sticky;top:0;z-index:20;display:flex;align-items:center;justify-content:space-between;
  gap:16px;padding:14px var(--pad);background:color-mix(in srgb,var(--paper) 88%,transparent);
  backdrop-filter:blur(10px);border-bottom:1px solid var(--line)}
.nav__brand{display:flex;align-items:center;gap:10px;text-decoration:none;
  font-family:var(--display);font-weight:800;font-size:15px;letter-spacing:-.02em}
.nav__brand em{color:var(--blue)}
.nav__links{display:flex;gap:clamp(14px,3vw,30px)}
.nav__links a{font-family:var(--mono);font-size:12px;letter-spacing:.04em;text-transform:uppercase;
  text-decoration:none;color:var(--ink-2);padding:4px 0;border-bottom:1.5px solid transparent}
.nav__links a:hover{color:var(--ink);border-bottom-color:var(--blue)}

/* ---------- shared ---------- */
.eyebrow{font-family:var(--mono);font-size:11px;letter-spacing:.16em;text-transform:uppercase;
  color:var(--blue);margin:0 0 18px}
.section{max-width:var(--maxw);margin:0 auto;padding:clamp(64px,11vw,140px) var(--pad)}
.section__head{margin-bottom:clamp(32px,5vw,56px);max-width:52ch}
.section h2{font-size:clamp(30px,5.4vw,56px)}
.section__lead{color:var(--ink-2);font-size:clamp(15px,1.7vw,18px);max-width:56ch;margin:18px 0 0}

.btn{display:inline-block;font-family:var(--mono);font-size:12px;letter-spacing:.08em;
  text-transform:uppercase;text-decoration:none;padding:14px 22px;background:var(--blue);color:#fff;
  border:1.5px solid var(--blue);transition:background .18s ease,color .18s ease,transform .18s ease}
.btn:hover{background:var(--blue-ink);border-color:var(--blue-ink);transform:translateY(-1px)}
.btn--quiet{background:transparent;color:var(--ink);border-color:var(--line)}
.btn--quiet:hover{background:transparent;color:var(--blue);border-color:var(--blue)}
.btn--big{font-size:clamp(13px,1.8vw,16px);padding:18px 28px;text-transform:none;letter-spacing:0;margin-top:28px}

/* ---------- hero ---------- */
.hero{max-width:var(--maxw);margin:0 auto;padding:clamp(44px,8vw,92px) var(--pad) clamp(32px,5vw,56px);
  position:relative}
.hero__mark{margin-bottom:clamp(22px,4vw,36px)}
.hero h1{font-size:clamp(40px,8.6vw,96px);max-width:13ch}
.hero h1 em{color:var(--blue)}
.hero__lead{color:var(--ink-2);font-size:clamp(16px,2vw,20px);max-width:56ch;margin:clamp(22px,3.5vw,32px) 0 0}
.hero__cta{display:flex;flex-wrap:wrap;gap:12px;margin-top:clamp(28px,4vw,40px)}

/* ---------- what we take on ---------- */
.worklist{list-style:none;margin:0;padding:0;display:grid;gap:0;border-top:1px solid var(--line)}
.work{display:grid;grid-template-columns:minmax(52px,.08fr) minmax(0,1fr);gap:clamp(14px,3vw,32px);
  padding:clamp(24px,3.5vw,36px) 0;border-bottom:1px solid var(--line);position:relative}
.work__bar{position:absolute;left:0;top:-1px;height:2px;width:0;background:var(--blue);
  transition:width .55s cubic-bezier(.2,.7,.2,1)}
.work.reveal--in .work__bar{width:100%}
.work__num{font-family:var(--mono);font-size:12px;color:var(--blue);letter-spacing:.08em;padding-top:6px}
.work__body h3{font-size:clamp(19px,2.7vw,27px);margin-bottom:10px}
.work__body p{color:var(--ink-2);margin:0;max-width:62ch}

/* honest status line - no venture is named until it ships */
.note{display:flex;align-items:flex-start;gap:12px;margin:clamp(30px,4vw,44px) 0 0;
  font-family:var(--mono);font-size:13px;line-height:1.7;color:var(--ink-2);max-width:62ch}
.note__dot{flex:none;width:7px;height:7px;margin-top:8px;background:var(--signal);border-radius:50%}
.note--contact{margin-top:clamp(26px,4vw,36px);font-size:13.5px}

/* ---------- approach ---------- */
.section--dark{background:var(--dark);color:var(--paper-2);
  padding:clamp(64px,11vw,140px) var(--pad)}
/* explicit wrapper, NOT a child-selector rule - .principles sets margin:0 later in the
   sheet and silently cancelled the auto-centering, throwing this whole section
   out of alignment with every other one. */
.section--dark .wrap{max-width:var(--maxw);margin:0 auto}
.section--dark .eyebrow{color:#7f93ff}
.section--dark .section__lead{color:#9aa6ac}
.principles{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));
  gap:1px;background:#222a30;border:1px solid #222a30;margin:0}
.principle{background:var(--dark);padding:clamp(22px,3vw,32px)}
.principle dt{font-family:var(--display);font-weight:600;font-size:clamp(17px,2.2vw,21px);
  letter-spacing:-.02em;margin-bottom:10px}
.principle dd{margin:0;color:#9aa6ac;font-size:15px}

/* ---------- contact ---------- */
.section--contact h2{font-size:clamp(30px,6vw,62px)}

/* ---------- footer ---------- */
.footer{display:flex;align-items:center;gap:12px;flex-wrap:wrap;max-width:var(--maxw);
  margin:0 auto;padding:26px var(--pad) 44px;border-top:1px solid var(--line);
  font-family:var(--mono);font-size:12px;color:var(--ink-2)}
.footer__sp{flex:1}

/* ---------- reveal ---------- */
.reveal{opacity:0;transform:translateY(14px);
  transition:opacity .6s cubic-bezier(.2,.7,.2,1) var(--d,0ms),transform .6s cubic-bezier(.2,.7,.2,1) var(--d,0ms)}
.reveal--in{opacity:1;transform:none}

@media (max-width:720px){
  .work{grid-template-columns:minmax(0,1fr);gap:10px}
  .nav__links{gap:14px}
}

@media (prefers-reduced-motion: reduce){
  html{scroll-behavior:auto}
  .reveal{opacity:1;transform:none;transition:none}
  .mark--in .mark__ghost,.mark--in .mark__front,.mark--in .mark__fill{animation:none}
  .mark__front{stroke-dashoffset:0}
  .work__bar{transition:none}
  .btn{transition:none}
}
`;
